import type { FiatFlow } from '@funkit/connect-core'
import { useEffect, useState } from 'react'

type ReplayableFlow = Pick<FiatFlow, 'phase' | 'collected'>

/**
 * Whether a send that failed in transit can go out again as it was.
 *
 * A replay re-sends the body under its idempotency key, so a server that took
 * the first request but lost the answer sees one attempt rather than two. That
 * only holds while the body is still the one that failed: an edit since makes
 * it a new request, which has to be sent afresh under a key of its own.
 */
export function useReplayableFailure(flow: ReplayableFlow): boolean {
  const isRetryable = flow.phase === 'RETRYABLE'
  const [failedWith, setFailedWith] = useState<ReplayableFlow['collected']>()

  useEffect(() => {
    // Snapshotted as the failure lands; the reducer hands out a new object
    // per edit, so identity is what says whether anything changed since.
    setFailedWith(isRetryable ? flow.collected : undefined)
  }, [isRetryable])

  return isRetryable && failedWith === flow.collected
}
